import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import JSZip from 'jszip';

import { bills, reports, type ReimbursementRow } from '../api/client';
import { BillViewer } from '../components/BillViewer';
import { BalanceCard } from '../features/balance/BalanceCard';
import { PaymentDrawer } from '../components/PaymentDrawer';
import { useToast } from '../components/Toast';
import { Badge, Button, EmptyState, ErrorState, Spinner, Input } from '../components/ui';
import { buildReimbursementCsv, downloadBlob } from '../lib/csv';
import { formatAmount, formatCompact, formatDate } from '../lib/format';
import { isoDateOnly, monthsAgo } from '../lib/query-params';
import { useQueryParams } from '../lib/query-params';
import { groupReimbursementRows, type ReportGroup } from '../lib/report-groups';
import { useExpandedGroups } from '../lib/use-expanded-groups';

/**
 * The reimbursement report ("pay me back"): period → payments grouped under
 * their vendor group, plus CSV / ZIP-of-bills / print exports. Clicking a row
 * opens the same payment drawer the matrix uses.
 */

/** Subscription, Date, Amount, ₽ equivalent, Rate, Bills, Type. */
const COLUMN_COUNT = 7;

const PRESETS = [
  { label: 'Last 2 months', value: `${monthsAgo(2)}|${isoDateOnly()}` },
  { label: 'Last 6 months', value: `${monthsAgo(6)}|${isoDateOnly()}` },
  {
    label: 'This year',
    value: `${isoDateOnly(new Date(new Date().getFullYear(), 0, 1))}|${isoDateOnly()}`,
  },
] as const;
export function ReportsPage() {
  const { params, setParams } = useQueryParams();
  const { toast } = useToast();
  const [billId, setBillId] = useState<string | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);
  const [zipping, setZipping] = useState(false);
  // Groups fold by default; only what the user opened is remembered.
  const { expanded, toggle } = useExpandedGroups();

  const from = params.from ?? monthsAgo(2);
  const to = params.to ?? isoDateOnly();

  const report = useQuery({
    queryKey: ['reports', 'reimbursement', from, to],
    queryFn: ({ signal }) => reports.reimbursement(from, to, signal),
  });

  const rows = report.data?.rows ?? [];
  const totals = report.data?.totals;
  const billCount = useMemo(() => rows.reduce((sum, row) => sum + row.bills.length, 0), [rows]);
  const groups = useMemo(() => groupReimbursementRows(rows), [rows]);

  const downloadCsv = () => {
    if (!report.data) return;
    const filename = `reimbursement-${from}-${to}.csv`;
    downloadBlob(buildReimbursementCsv(report.data), filename, 'text/csv;charset=utf-8');
    toast(`CSV saved (${filename})`, 'success');
  };

  const downloadZip = async () => {
    if (rows.length === 0) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      const used = new Set<string>();
      let failures = 0;
      for (const row of rows) {
        for (const bill of row.bills) {
          let name = `${row.subscriptionName.replace(/[/\\:*?"<>|]/g, '_')} — ${formatDate(row.occurredAt)} — ${bill.originalName ?? `${bill.id}.pdf`}`;
          while (used.has(name)) name = name.replace(/(\.[a-z0-9]+)?$/i, (ext) => ` (2)${ext}`);
          used.add(name);
          try {
            // The presigned redirect is followed, so this is the object body.
            const response = await fetch(bills.contentUrl(bill.id));
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            zip.file(name, await response.arrayBuffer());
          } catch {
            failures += 1;
          }
        }
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      downloadBlob(blob, `bills-${from}-${to}.zip`, 'application/zip');
      toast(failures > 0 ? `ZIP saved, ${failures} bill(s) could not be fetched` : `ZIP saved (${used.size} bills)`, failures > 0 ? 'info' : 'success');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'ZIP failed', 'error');
    } finally {
      setZipping(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Reimbursement report</h1>
          <p className="page-header__sub">
            {report.data
              ? `${rows.length} payment${rows.length === 1 ? '' : 's'} · ${billCount} bill${billCount === 1 ? '' : 's'} attached`
              : 'Pick a period and export what you paid for the company.'}
          </p>
          {/* Print only: the paper copy has to state the period it covers. */}
          <p className="report-period">Period: {formatDate(from)} — {formatDate(to)}</p>
        </div>
        <div className="page-header__actions no-print">
          <Button variant="secondary" onClick={() => window.print()} disabled={!report.data}>
            Print
          </Button>
          <Button variant="secondary" onClick={downloadZip} loading={zipping} disabled={!report.data || billCount === 0}>
            ZIP of bills
          </Button>
          <Button variant="primary" onClick={downloadCsv} disabled={!report.data}>
            Download CSV
          </Button>
        </div>
      </div>

      <div className="toolbar no-print">
        <div className="toolbar__field">
          <label className="toolbar__label" htmlFor="report-from">Period start</label>
          <Input id="report-from" type="date" value={from} onChange={(event) => setParams({ from: event.target.value || undefined })} />
        </div>
        <div className="toolbar__field">
          <label className="toolbar__label" htmlFor="report-to">Period end</label>
          <Input id="report-to" type="date" value={to} onChange={(event) => setParams({ to: event.target.value || undefined })} />
        </div>
        <div className="toolbar__spacer" />
        <div className="toolbar__field">
          <label className="toolbar__label" htmlFor="report-preset">Preset</label>
          <select
            id="report-preset"
            className="input"
            value={`${from}|${to}`}
            onChange={(event) => {
              const [nextFrom, nextTo] = event.target.value.split('|');
              setParams({ from: nextFrom === '' ? undefined : nextFrom, to: nextTo === '' ? undefined : nextTo });
            }}
          >
            {/* Matrix month headers land on arbitrary periods, so the select
                needs an honest label instead of silently showing "Last 2
                months" for a period that is not that preset. */}
            {PRESETS.every((preset) => preset.value !== `${from}|${to}`) ? (
              <option value={`${from}|${to}`}>Custom period</option>
            ) : null}
            {PRESETS.map((preset) => (
              <option key={preset.label} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Same from/to as the report above it: the balance is period-scoped, and
          the card itself prints the balance carried into the window. */}
      <BalanceCard from={from} to={to} />

      {totals ? (
        <div className="kpi-row">
          <div className="kpi">
            <div className="kpi__num">{formatAmount(totals.rubTotal, 'RUB')}</div>
            <div className="kpi__label">Total for the period (₽)</div>
          </div>
          <div className="kpi">
            {/* One line per currency: the joined string set at 26px wrapped into
                three shouting lines. */}
            <div className="kpi__num kpi__num--stack">
              {Object.keys(totals.paidByCurrency).length === 0 ? (
                '—'
              ) : (
                Object.entries(totals.paidByCurrency).map(([currency, amount]) => (
                  <span className="kpi__line" key={currency}>
                    {formatAmount(amount, currency)}
                  </span>
                ))
              )}
            </div>
            <div className="kpi__label">Paid in original currencies</div>
          </div>
          <div className="kpi">
            <div className="kpi__num">{totals.subscriptionsOnly.eventsCount}</div>
            <div className="kpi__label">Subscription payments</div>
          </div>
          <div className="kpi">
            <div className="kpi__num">{totals.commissions.eventsCount}</div>
            <div className="kpi__label">Card funding / commissions</div>
          </div>
        </div>
      ) : null}

      <div className="card card--flush">
        {report.isPending ? (
          <Spinner label="Building the report" />
        ) : report.isError ? (
          <ErrorState message={report.error.message} onRetry={() => void report.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState title="No payments in this period" hint="Move the period, or record payments on the Payments page." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Subscription</th>
                  <th scope="col">Date</th>
                  <th scope="col" className="num">Amount</th>
                  <th scope="col" className="num">₽ equivalent</th>
                  <th scope="col" className="num">Rate</th>
                  <th scope="col">Bills</th>
                  <th scope="col">Type</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <ReportGroupBlock
                    key={group.name}
                    group={group}
                    collapsed={!expanded.has(group.name)}
                    onToggle={toggle}
                    onOpenEvent={setEventId}
                    onOpenBill={setBillId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totals ? (
        <div className="card no-print">
          <h2 className="card__title">Totals</h2>
          <div className="report-totals">
            <TotalsCard
              label="Subscriptions only"
              paidByCurrency={totals.subscriptionsOnly.paidByCurrency}
              rubTotal={totals.subscriptionsOnly.rubTotal}
            />
            <TotalsCard
              label="Card funding / commissions"
              paidByCurrency={totals.commissions.paidByCurrency}
              rubTotal={totals.commissions.rubTotal}
            />
            <TotalsCard label="Grand total" paidByCurrency={totals.paidByCurrency} rubTotal={totals.rubTotal} />
          </div>
        </div>
      ) : null}

      <BillViewer billId={billId} onClose={() => setBillId(null)} />

      {/* The very drawer the payments matrix opens: legs, rates, bills. The
          report only points at it, it does not reimplement it. */}
      <PaymentDrawer eventId={eventId} onClose={() => setEventId(null)} />
    </>
  );
}

function TotalsCard({
  label,
  paidByCurrency,
  rubTotal,
}: {
  label: string;
  paidByCurrency: Record<string, number>;
  rubTotal: number;
}) {
  return (
    <div className="totals-card">
      <div className="totals-card__label">{label}</div>
      <div className="totals-card__value">{formatAmount(rubTotal, 'RUB')}</div>
      <ul className="totals-card__lines">
        {Object.entries(paidByCurrency).map(([currency, amount]) => (
          <li key={currency}>
            {formatAmount(amount, currency)} paid in {currency}
          </li>
        ))}
        {Object.keys(paidByCurrency).length === 0 ? <li>nothing recorded</li> : null}
      </ul>
    </div>
  );
}

/**
 * A vendor-group header row followed by that group's payments. Collapsed, the
 * header alone survives — which is also what prints, so a collapsed group is a
 * one-line rollup on paper rather than a missing section.
 */
function ReportGroupBlock({
  group,
  collapsed,
  onToggle,
  onOpenEvent,
  onOpenBill,
}: {
  group: ReportGroup;
  collapsed: boolean;
  onToggle: (name: string) => void;
  onOpenEvent: (eventId: string) => void;
  onOpenBill: (billId: string) => void;
}) {
  const currencies = Object.entries(group.paidByCurrency)
    .map(([currency, amount]) => formatAmount(amount, currency))
    .join(' + ');

  return (
    <>
      <tr className="group-row">
        <th scope="colgroup" colSpan={COLUMN_COUNT}>
          <button
            type="button"
            className="report-group__toggle"
            aria-expanded={!collapsed}
            title={collapsed ? 'Show the payments in this group' : 'Hide the payments in this group'}
            onClick={() => onToggle(group.name)}
          >
            <span className="report-group__chevron" aria-hidden="true">
              {collapsed ? '▸' : '▾'}
            </span>
            <span className="report-group__name">{group.label}</span>
          </button>
          <span className="report-group__meta">
            {group.eventsCount} payment{group.eventsCount === 1 ? '' : 's'}
            <span className="report-group__amounts">
              {formatCompact(group.rubTotal, 'RUB')}
              {currencies ? <span className="matrix__rub">{currencies}</span> : null}
            </span>
          </span>
        </th>
      </tr>
      {collapsed
        ? null
        : group.rows.map((row) => (
            <ReportRow
              key={row.paymentEventId}
              row={row}
              onOpenEvent={onOpenEvent}
              onOpenBill={onOpenBill}
            />
          ))}
    </>
  );
}

function ReportRow({
  row,
  onOpenEvent,
  onOpenBill,
}: {
  row: ReimbursementRow;
  onOpenEvent: (eventId: string) => void;
  onOpenBill: (billId: string) => void;
}) {
  /** The row opens the drawer; the bill thumbnails inside it keep their own job. */
  const open = () => onOpenEvent(row.paymentEventId);
  return (
    <tr
      className={`report-row${row.isCommission ? ' row--blocked' : ''}`}
      data-payment-event-id={row.paymentEventId}
      tabIndex={0}
      aria-label={`Payment details: ${row.subscriptionName}, ${formatDate(row.occurredAt)}`}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
    >
      <td>
        {/* No group name here: the header row above the block already says it. */}
        <span className="cell-primary">{row.subscriptionName}</span>
      </td>
      <td className="cell-muted">{formatDate(row.occurredAt)}</td>
      <td className="num">{formatAmount(row.paidAmount, row.paidCurrency)}</td>
      <td className="num">
        {row.rubAmount === null ? <span className="cell-muted">—</span> : formatAmount(row.rubAmount, 'RUB')}
      </td>
      <td className="num">
        {row.fxRate === null ? (
          <span className="cell-muted">—</span>
        ) : (
          <>
            {row.fxRate}
            {row.rateSource ? (
              <span className="matrix__rub">
                <Badge tone={row.rateSource === 'MANUAL' ? 'warn' : 'brand'}>{row.rateSource}</Badge>
              </span>
            ) : null}
          </>
        )}
      </td>
      <td>
        {row.bills.length === 0 ? (
          <span className="cell-muted">—</span>
        ) : (
          <span className="bills" style={{ marginTop: 0 }}>
            {row.bills.map((bill) => (
              <button
                type="button"
                className="bill-thumb"
                key={bill.id}
                onClick={(event) => {
                  // The thumbnail is a bill, not the row: it must not drag the
                  // payment drawer up behind the viewer.
                  event.stopPropagation();
                  onOpenBill(bill.id);
                }}
              >
                {bill.originalName ?? bill.id.slice(0, 8)}
              </button>
            ))}
          </span>
        )}
      </td>
      <td>
        <Badge tone={row.isCommission ? 'warn' : 'ok'}>{row.isCommission ? 'commission' : 'subscription'}</Badge>
      </td>
    </tr>
  );
}
